/* One custom-apparel form: inquiry or full account setup. Native Shopify submission. */
(() => {
  'use strict';
  const STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];
  const LIVE_KEY = 'voler-full-custom-handoff-v1';
  const PREVIEW_KEY = 'voler-full-custom-preview-handoff-v1';
  const MAX_AGE = 2 * 60 * 60 * 1000;

  function read(key) {
    try {
      const value = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (!value || !Number.isFinite(value.savedAt) || Date.now() - value.savedAt > MAX_AGE) {
        sessionStorage.removeItem(key);
        return null;
      }
      return value;
    } catch { return null; }
  }
  function save(key, value) {
    try { sessionStorage.setItem(key, JSON.stringify({ ...value, savedAt: Date.now() })); return true; }
    catch { return false; }
  }
  function reference() {
    if (crypto.randomUUID) return 'FC-' + crypto.randomUUID().replaceAll('-', '').slice(0, 12).toUpperCase();
    if (crypto.getRandomValues) {
      const bytes = crypto.getRandomValues(new Uint8Array(6));
      return 'FC-' + [...bytes].map(byte => byte.toString(16).padStart(2, '0')).join('').toUpperCase();
    }
    throw new Error('Secure reference generation unavailable');
  }
  function prefillFrom(valuesToSend) {
    return Object.fromEntries(['team','name','email','phone','artAddress1','artAddress2','artCity','artState','artZip']
      .map(key => [key, valuesToSend[key] || '']));
  }
  function init(root) {
    if (root.dataset.vfiInitialized) return;
    root.dataset.vfiInitialized = 'true';
    const storageKey = root.dataset.vfiPreview === 'true' ? PREVIEW_KEY : LIVE_KEY;
    const form = root.querySelector('form');
    const content = root.querySelector('[data-vfi-content]');
    const success = root.querySelector('[data-vfi-success]');
    const fields = Object.fromEntries([...root.querySelectorAll('[data-vsr-intake]')]
      .map(field => [field.dataset.vsrIntake, field]));
    const error = root.querySelector('[data-vfi-error]');
    const submitButton = root.querySelector('[data-vsr-intake-submit]');
    const continueLink = root.querySelector('[data-vfi-continue]');
    const purposes = [...root.querySelectorAll('[data-vfi-purpose]')];
    const modeContent = root.querySelector('[data-vfi-mode-content]');
    const setupOnly = [...root.querySelectorAll('[data-vfi-setup-only]')];
    const inquiryOnly = [...root.querySelectorAll('[data-vfi-inquiry-only]')];
    const initialRequired = new Map(Object.values(fields).map(field => [field, field.required]));
    const common = new Set(['team', 'name', 'email', 'phone', 'comments', 'customerType', 'orderSize']);
    const inquiryKeys = new Set(['artHelp', 'communicationPreference']);
    const purpose = () => purposes.find(radio => radio.checked)?.value || '';
    fields.artState.insertAdjacentHTML('beforeend', STATES.map(state => '<option value="' + state + '">' + state + '</option>').join(''));

    function showError(message) {
      error.textContent = message;
      error.hidden = false;
      error.focus();
    }
    function updateLeague() {
      const other = purpose() === 'setup' && fields.league.value === 'Other Youth MTB League';
      root.querySelector('[data-vsr-other-league]').hidden = !other;
      fields.leagueName.required = other;
      fields.leagueName.disabled = !other;
    }
    function values(activeOnly = false) {
      return { ...Object.fromEntries(Object.entries(fields)
        .filter(([, field]) => !activeOnly || !field.disabled)
        .map(([key, field]) => [key, field.value.trim()])), purpose: purpose() };
    }
    function fill(valuesToRestore) {
      for (const [key, value] of Object.entries(valuesToRestore || {})) {
        if (fields[key]) fields[key].value = value;
      }
      // Existing drafts from the previous setup-only form retain their meaning.
      const restoredPurpose = valuesToRestore?.purpose || 'setup';
      purposes.forEach(radio => { radio.checked = radio.value === restoredPurpose; });
      updateMode();
    }
    function updateSubmitLabel() {
      submitButton.textContent = purpose() === 'inquiry' ? 'Send inquiry' : fields.needSamples.value === 'Yes'
        ? 'Submit & choose sizing samples'
        : 'Submit account setup';
    }
    function updateCommunicationPreference() {
      const prefersPhone = purpose() === 'inquiry' && fields.communicationPreference.value === 'Phone';
      fields.phone.required = !fields.phone.disabled && (purpose() === 'setup' || prefersPhone);
      root.querySelector('[data-vfi-phone-label]').textContent = purpose() === 'setup'
        ? 'Primary contact phone number *' : prefersPhone ? 'Phone number *' : 'Phone number (optional)';
    }
    function updateMode() {
      const mode = purpose();
      const setup = mode === 'setup';
      fields.name.setCustomValidity('');
      fields.comments.setCustomValidity('');
      modeContent.hidden = !mode;
      setupOnly.forEach(container => { container.hidden = !setup; });
      inquiryOnly.forEach(container => { container.hidden = mode !== 'inquiry'; });
      for (const [key, field] of Object.entries(fields)) {
        field.disabled = !mode || (setup ? inquiryKeys.has(key) : !common.has(key) && !inquiryKeys.has(key));
        field.required = !field.disabled && (setup ? initialRequired.get(field) : ['name', 'email', 'comments'].includes(key));
      }
      updateLeague();
      root.querySelector('[data-vfi-team-label]').textContent = setup ? 'New customer / organization name *' : 'Team or organization (optional)';
      updateCommunicationPreference();
      root.querySelector('[data-vfi-customer-type-label]').textContent = setup ? 'Customer type *' : 'Customer type (optional)';
      root.querySelector('[data-vfi-comments-label]').textContent = setup ? 'Additional comments / setup requests' : 'What would you like to know? *';
      fields.comments.placeholder = setup ? 'Fundraising plans, team-funded garment credits, or other requests' : 'Tell us about your project, questions, or plans';
      fields.comments.name = setup ? 'contact[Additional comments / setup requests]' : 'contact[Inquiry message]';
      for (const selector of ['[data-vfi-mode-heading]', '[data-vfi-mode-copy]']) {
        const element = root.querySelector(selector);
        if (mode) element.textContent = element.dataset[mode];
      }
      root.querySelector('[data-vfi-mode-eyebrow]').textContent = setup ? 'Full Custom intake' : 'Custom apparel inquiry';
      root.querySelector('[data-vfi-request-type]').value = setup ? 'Full Custom account setup' : 'Custom Apparel Inquiry';
      root.querySelector('[data-vfi-fulfillment]').textContent = setup ? 'Submitting sends this intake to Voler Sales.' : 'Your inquiry will be sent to Voler Sales. No account setup or sample request is submitted.';
      updateSubmitLabel();
    }
    function continueToSamples() {
      try {
        const destination = new URL(continueLink.href, window.location.href);
        if (destination.origin !== window.location.origin || destination.pathname !== '/pages/sizing-samples') return false;
        window.location.assign(destination.href);
        return true;
      } catch { return false; }
    }
    function showSuccess(mode = 'setup') {
      content.hidden = true;
      success.hidden = false;
      const preview = root.dataset.vfiPreview === 'true';
      root.querySelector('[data-vfi-success-eyebrow]').textContent = mode === 'inquiry' ? 'Inquiry received' : 'Intake received';
      root.querySelector('[data-vfi-success-copy]').textContent = mode === 'inquiry'
        ? (preview ? 'Preview complete. No email was sent. Voler Sales would receive your inquiry.' : 'Thank you for your interest in custom apparel. Voler Sales has your inquiry and will follow up with you.')
        : preview
        ? 'Preview complete. No email was sent. You can continue to the separate sizing sample preview; no kits have been requested.'
        : 'Voler Sales has your intake. If you would like sizing samples, continue below. No sample request has been sent yet.';
      continueLink.hidden = mode === 'inquiry';
      success.focus();
    }
    function body() {
      return [
        purpose() === 'inquiry' ? 'CUSTOM APPAREL INQUIRY — Sales follow-up requested' : 'FULL CUSTOM ACCOUNT SETUP — manual review required',
        '',
        purpose() === 'inquiry' ? 'This is an inquiry only. No account setup, order or sizing sample request was placed.' : 'This is an intake only. No order or sizing sample request was placed.',
      ].join('\n');
    }

    fields.league.addEventListener('change', updateLeague);
    fields.needSamples.addEventListener('change', updateSubmitLabel);
    fields.communicationPreference.addEventListener('change', updateCommunicationPreference);
    for (const field of [fields.name, fields.comments]) {
      field.addEventListener('input', () => field.setCustomValidity(''));
    }
    purposes.forEach(radio => radio.addEventListener('change', updateMode));
    const pending = read(storageKey);
    if (content.dataset.vfiPostedSuccess === 'true') {
      if (pending && ['draft', 'submitting'].includes(pending.stage)) {
        const mode = pending.values.purpose || 'setup';
        const saved = save(storageKey, mode === 'inquiry'
          ? { stage: 'inquiry-received', reference: pending.reference, purpose: mode }
          : { stage: 'ready', reference: pending.reference, purpose: mode, prefill: prefillFrom(pending.values) });
        if (mode === 'setup' && saved && pending.values.needSamples === 'Yes' && continueToSamples()) return;
        showSuccess(mode);
        return;
      }
      // Do not guess the purpose or redirect when confirmation state is missing.
      showSuccess(pending?.purpose || 'unknown');
      if (!pending) {
        continueLink.hidden = true;
        root.querySelector('[data-vfi-success-eyebrow]').textContent = 'Request received';
        root.querySelector('[data-vfi-success-copy]').textContent = 'Thank you. Voler Sales has received your request and will follow up with you.';
      }
      return;
    }
    const resumable = pending && ['draft', 'submitting'].includes(pending.stage) ? pending : null;
    if (resumable) fill(resumable.values);
    else updateMode();
    let currentReference;
    try { currentReference = resumable?.reference || reference(); }
    catch {
      showError('Unable to create a reference in this browser. Please contact Voler Sales; nothing was sent.');
      return;
    }
    function syncDraft() {
      const currentValues = values();
      root.querySelector('[data-vfi-reference]').value = currentReference;
      root.querySelector('[data-vfi-body]').value = body();
      return save(storageKey, { stage: 'draft', reference: currentReference, values: currentValues });
    }
    if (!syncDraft()) {
      showError('This browser cannot retain the intake during submission. Please enable session storage; nothing was sent.');
      return;
    }
    form.addEventListener('input', syncDraft);
    form.addEventListener('change', syncDraft);
    content.hidden = false;
    submitButton.disabled = false;
    updateSubmitLabel();

    form.addEventListener('submit', event => {
      error.hidden = true;
      updateMode();
      if (!purpose()) {
        event.preventDefault();
        purposes[0].reportValidity();
        purposes[0].focus();
        return;
      }
      for (const field of Object.values(fields)) {
        if ([fields.name, fields.comments].includes(field) && !field.disabled && field.required && !field.value.trim()) {
          field.setCustomValidity('Please enter a value, not only spaces.');
        }
        if (!field.disabled && !field.checkValidity()) {
          event.preventDefault();
          field.reportValidity();
          field.focus();
          return;
        }
      }
      const submitted = values(true);
      const ref = currentReference;
      if (!save(storageKey, { stage: 'submitting', reference: ref, values: submitted })) {
        event.preventDefault();
        showError('This browser cannot retain the intake during submission. Please enable session storage; nothing was sent.');
        return;
      }
      const legacyEmailField = root.querySelector('[data-vfi-email]');
      if (legacyEmailField) legacyEmailField.value = submitted.email;
      const legacyNameField = root.querySelector('[data-vfi-name]');
      if (legacyNameField) legacyNameField.value = submitted.name;
      const legacyPhoneField = root.querySelector('[data-vfi-phone]');
      if (legacyPhoneField) legacyPhoneField.value = submitted.phone;
      const legacyOrganizationField = root.querySelector('[data-vfi-organization]');
      if (legacyOrganizationField) legacyOrganizationField.value = submitted.team;
      root.querySelector('[data-vfi-reference]').value = ref;
      root.querySelector('[data-vfi-body]').value = body();
      if (root.dataset.vfiPreview === 'true') {
        event.preventDefault();
        const saved = save(storageKey, submitted.purpose === 'inquiry'
          ? { stage: 'inquiry-received', reference: ref, purpose: 'inquiry' }
          : { stage: 'ready', reference: ref, purpose: 'setup', prefill: prefillFrom(submitted) });
        if (submitted.purpose === 'setup' && saved && submitted.needSamples === 'Yes' && continueToSamples()) return;
        showSuccess(submitted.purpose);
        return;
      }
      submitButton.disabled = true;
      submitButton.textContent = purpose() === 'inquiry' ? 'Sending inquiry…' : 'Sending intake…';
    });
  }
  document.querySelectorAll('[data-vfi]').forEach(init);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-vfi]').forEach(init));
})();
